/**
 * AI 평가 구성 1단계: 관리자 학습내용(admin_learning_content_rows)에서
 * 평가계획에 선택된 성취기준을 포함하는 단원·학습내용을 찾습니다 (AI 사용 없음).
 *
 * achievement_text 형식(실데이터 확인): "[6과05-01] 문장\r\n[6과05-02] 문장…" — 코드+문장, 줄바꿈 구분.
 * 1차: 성취기준 코드 매칭 / 2차(코드 없는 행): 공백·기호 제거한 문장 매칭.
 */
import type { AdminLearningContentRow, AdminLearningContentSubCategory } from '../types/domain';
import type { EvaluationStandardDraft } from '../types/evaluationPlan';
import { getAdminLearningContentRows, getAdminLearningContentTemplates } from '../services/adminLearningContentService';
import { getWorkspaceLearningContentRows, getWorkspaceLearningContentTemplates } from '../services/workspaceLearningContentService';

export type LearningContentSource = 'workspace' | 'admin';

type GradeNumber = 1 | 2 | 3 | 4 | 5 | 6;

const CODE_RE = /\[?\s*(\d{1,2})\s*([가-힣]{1,3})\s*(\d{2})\s*[-–]\s*(\d{2})\s*\]?/g;

/** '[ 6과05-01 ]' / '6과 05-01' → '6과05-01' */
export function normalizeAchievementCode(raw: string): string {
  CODE_RE.lastIndex = 0;
  const m = CODE_RE.exec(String(raw ?? ''));
  CODE_RE.lastIndex = 0;
  return m ? `${m[1]}${m[2]}${m[3]}-${m[4]}` : '';
}

export function extractAchievementCodes(text: string): string[] {
  const out = new Set<string>();
  const re = new RegExp(CODE_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(String(text ?? '')))) out.add(`${m[1]}${m[2]}${m[3]}-${m[4]}`);
  return Array.from(out);
}

/** 문장 비교용: 코드·공백·문장부호 제거, ⋅ → · */
export function normalizeStandardSentence(text: string): string {
  return String(text ?? '')
    .replace(new RegExp(CODE_RE.source, 'g'), '')
    .replace(/\u22c5/g, '·')
    .replace(/[\s.,·~\-–()[\]「」'"“”‘’]/g, '')
    .trim();
}

const SUBJECT_BY_CODE_CHAR: Record<string, string> = {
  국: '국어', 수: '수학', 사: '사회', 과: '과학', 도: '도덕', 체: '체육', 음: '음악', 미: '미술', 영: '영어', 실: '실과',
};
const INTEGRATED_CHARS = new Set(['바', '슬', '즐']);
const KNOWN_SUBJECTS = new Set(Object.values(SUBJECT_BY_CODE_CHAR));

function gradesFromCode(code: string): GradeNumber[] {
  const n = Number(/^(\d{1,2})/.exec(code)?.[1] ?? 0);
  if (n === 2) return [1, 2];
  if (n === 4) return [3, 4];
  if (n === 6) return [5, 6];
  if (n >= 1 && n <= 6) return [n as GradeNumber];
  return [];
}

const squash = (s: string) => String(s ?? '').replace(/\s+/g, '');

export type LearningContentMatchLesson = {
  key: string;
  gradeNumber: GradeNumber;
  subCategory: AdminLearningContentSubCategory;
  templateName: string;
  trackText: string;
  unitText: string;
  learningText: string;
  matchedCodes: string[];
  /** 같은 단원의 차시 목록(프롬프트 맥락용) */
  orderIndex: number;
};

export type LearningContentMatchUnit = {
  key: string;
  source: LearningContentSource;
  gradeNumber: GradeNumber;
  subCategory: AdminLearningContentSubCategory;
  templateName: string;
  unitText: string;
  lessons: LearningContentMatchLesson[];
  matchedCodes: string[];
};

const SUB_LABEL: Record<AdminLearningContentSubCategory, string> = { common: '공통', term1: '1학기', term2: '2학기' };
export const learningSubCategoryLabel = (s: AdminLearningContentSubCategory) => SUB_LABEL[s] ?? s;

/**
 * 선택 성취기준을 포함하는 관리자 학습내용을 단원별로 묶어 반환.
 * 템플릿(교과명) 선택: 코드의 교과 글자/카테고리명과 같은 템플릿, 통합교과(바·슬·즐)는 교과명 템플릿이 아닌 것 전부.
 */
export async function findLearningContentsForStandards(params: {
  workspaceId: string;
  standards: EvaluationStandardDraft[];
  categoryName: string;
  /** 사용자(워크스페이스) 화면: 현재 학급 학년만. 내 학습내용 우선, 없으면 관리자 학습내용(그 학년만) */
  classScope?: { gradeId: string; gradeNumbers: number[] } | null;
}): Promise<{ units: LearningContentMatchUnit[]; source: LearningContentSource; gradeNumbers: number[] }> {
  const { workspaceId, standards, categoryName, classScope } = params;
  const codes = new Set<string>();
  const sentences = new Map<string, string>();
  standards.forEach((s) => {
    const code = normalizeAchievementCode(s.achievement_code || s.achievement_text || '');
    if (code) codes.add(code);
    const sent = normalizeStandardSentence(s.achievement_text || '');
    if (sent.length >= 8) sentences.set(sent, code);
  });
  if (!codes.size && !sentences.size) return { units: [], source: 'admin', gradeNumbers: [] };

  const grades = new Set<GradeNumber>();
  const subjects = new Set<string>();
  let integrated = false;
  codes.forEach((code) => {
    gradesFromCode(code).forEach((g) => grades.add(g));
    const ch = /^\d{1,2}([가-힣])/.exec(code)?.[1] ?? '';
    if (INTEGRATED_CHARS.has(ch)) integrated = true;
    else if (SUBJECT_BY_CODE_CHAR[ch]) subjects.add(SUBJECT_BY_CODE_CHAR[ch]);
  });
  standards.forEach((s) => {
    const t = squash(s.track_text || '');
    if (t) subjects.add(t);
  });
  if (categoryName.trim()) subjects.add(squash(categoryName));
  const classGrades = (classScope?.gradeNumbers ?? []).filter((g): g is GradeNumber => g >= 1 && g <= 6) as GradeNumber[];
  const searchGrades: GradeNumber[] = classScope
    ? classGrades
    : grades.size
      ? Array.from(grades)
      : [1, 2, 3, 4, 5, 6];
  const subs: AdminLearningContentSubCategory[] = ['common', 'term1', 'term2'];

  type Scope = { g: GradeNumber; sub: AdminLearningContentSubCategory; tpl: string; rows: AdminLearningContentRow[] };
  const loadScopes = async (src: LearningContentSource): Promise<Scope[]> => {
    const gradeId = (classScope?.gradeId ?? '').trim();
    const jobs: Array<Promise<Scope[]>> = [];
    for (const g of searchGrades) {
      for (const sub of subs) {
        jobs.push(
          (async () => {
            const templates =
              src === 'workspace'
                ? await getWorkspaceLearningContentTemplates(workspaceId, gradeId, g, sub).catch(() => [])
                : await getAdminLearningContentTemplates(workspaceId, g, sub).catch(() => []);
            const names = templates.map((t) => t.template_name);
            let chosen = names.filter((n) => Array.from(subjects).some((s) => s && (squash(n) === s || s.includes(squash(n)) || squash(n).includes(s))));
            if (integrated) chosen = Array.from(new Set(chosen.concat(names.filter((n) => !KNOWN_SUBJECTS.has(squash(n))))));
            if (!chosen.length && !subjects.size) chosen = names;
            const out: Scope[] = [];
            for (const tpl of chosen) {
              const rows =
                src === 'workspace'
                  ? await getWorkspaceLearningContentRows(workspaceId, gradeId, g, sub, tpl).catch(() => [] as AdminLearningContentRow[])
                  : await getAdminLearningContentRows(workspaceId, g, sub, tpl).catch(() => [] as AdminLearningContentRow[]);
              out.push({ g, sub, tpl, rows });
            }
            return out;
          })()
        );
      }
    }
    return (await Promise.all(jobs)).flat();
  };
  let source: LearningContentSource = 'admin';
  let scopes: Scope[] = [];
  if (classScope && classScope.gradeId.trim() && workspaceId.trim() && searchGrades.length) {
    scopes = await loadScopes('workspace');
    source = 'workspace';
    // 해당 학년에 내 학습내용 행이 하나도 없으면 관리자 학습내용(같은 학년만)으로
    if (!scopes.some((sc) => sc.rows.length > 0)) {
      scopes = await loadScopes('admin');
      source = 'admin';
    }
  } else if (searchGrades.length) {
    scopes = await loadScopes('admin');
  }


  const units = new Map<string, LearningContentMatchUnit>();
  for (const scope of scopes) {
    for (const row of scope.rows) {
      const text = row.achievement_text || '';
      if (!text.trim()) continue;
      const rowCodes = extractAchievementCodes(text);
      let matched = rowCodes.filter((c) => codes.has(c));
      if (!matched.length && sentences.size) {
        const rowSent = normalizeStandardSentence(text);
        sentences.forEach((code, sent) => {
          if (rowSent.includes(sent)) matched.push(code || sent.slice(0, 12));
        });
        matched = Array.from(new Set(matched));
      }
      if (!matched.length) continue;
      const unitText = (row.unit_text || '').trim() || '(단원 없음)';
      const ukey = `${scope.g}|${scope.sub}|${scope.tpl}|${unitText}`;
      let unit = units.get(ukey);
      if (!unit) {
        unit = { key: ukey, source, gradeNumber: scope.g, subCategory: scope.sub, templateName: scope.tpl, unitText, lessons: [], matchedCodes: [] };
        units.set(ukey, unit);
      }
      unit.lessons.push({
        key: `${ukey}|${row.id}`,
        gradeNumber: scope.g,
        subCategory: scope.sub,
        templateName: scope.tpl,
        trackText: (row.track_text || '').trim(),
        unitText,
        learningText: (row.learning_text || '').trim(),
        matchedCodes: matched,
        orderIndex: Number(row.order_index ?? 0),
      });
      unit.matchedCodes = Array.from(new Set(unit.matchedCodes.concat(matched)));
    }
  }
  const list: LearningContentMatchUnit[] = Array.from(units.values());
  list.forEach((u) => u.lessons.sort((a, b) => a.orderIndex - b.orderIndex));
  // 매칭 코드가 많은 단원 우선, 다음 학년·학기 순
  list.sort((a, b) => b.matchedCodes.length - a.matchedCodes.length || a.gradeNumber - b.gradeNumber || a.subCategory.localeCompare(b.subCategory));
  return { units: list, source, gradeNumbers: searchGrades };
}
